import { useSearchParams } from '@remix-run/react';
import { createContext, useContext, useEffect, useState, type PropsWithChildren } from 'react';
import { isValidAffiliateRef } from './affiliate.ts';

/**
 * Holds the affiliate `?ref=` value for the lifetime of the page session.
 *
 * The source of truth is React memory on the persistent root component.
 * Because the root never unmounts during client-side navigation, the ref
 * survives navigating from a landing page to a voice shop, to the imprint and
 * back — independent of cookies or any device storage. This is therefore
 * consent-free: in-memory runtime state is not "storage in the terminal
 * equipment" under the ePrivacy directive.
 *
 * As a convenience, the remembered ref is also reflected back into the address
 * bar via `history.replaceState` (cosmetic only — it does not trigger a Remix
 * navigation or loader revalidation), so links the visitor copies/shares keep
 * the attribution. The consent-gated cookie in `affiliate.server.ts` remains a
 * separate, longer-lived fallback that survives full page reloads when
 * marketing consent was granted.
 */
const AffiliateRefContext = createContext<string | null>(null);

export function AffiliateRefProvider({ children }: PropsWithChildren) {
    const [ searchParams ] = useSearchParams();
    const urlRef = searchParams.get('ref');
    const [ ref, setRef ] = useState<string | null>(() => (isValidAffiliateRef(urlRef) ? urlRef : null));

    useEffect(() => {
        // Last-touch: a fresh, valid ref in the URL overrides the remembered one.
        if (isValidAffiliateRef(urlRef) && urlRef !== ref) {
            setRef(urlRef);
        }
    }, [ urlRef, ref ]);

    useEffect(() => {
        // Keep the address bar in sync with the remembered ref, without
        // triggering a Remix navigation / loader revalidation. `replaceState`
        // is purely cosmetic — the real attribution lives in `ref` above.
        if (typeof window === 'undefined' || !ref) {
            return;
        }
        const url = new URL(window.location.href);
        if (url.searchParams.get('ref') !== ref) {
            url.searchParams.set('ref', ref);
            window.history.replaceState(window.history.state, '', url.toString());
        }
    }, [ ref, urlRef ]);

    return <AffiliateRefContext.Provider value={ref}>{children}</AffiliateRefContext.Provider>;
}

/** Current affiliate ref for this page session, or `null` if none. */
export function useAffiliateRef(): string | null {
    return useContext(AffiliateRefContext);
}
